import React, { FC, useRef, useState, useEffect } from 'react';
import Button from 'src/components/Button';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { RootStoreType } from 'src/types/store';
import { useNavigate } from 'react-router-dom';
import { ReminderType } from 'src/types/reminder';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import FormBlock from 'src/components/FormBlock';
import {
    clearReminderDetail,
    getReminderDetailFetch,
    createReminderDetailFetch,
    updateReminderDetailFetch,
    cancelReminderDetailFetch,
    removeFromReminderAttendeesFetch,
} from 'src/resources/reminderDetail/reducer';
import Dialog from 'src/components/Dialog';
import Detail from './Detail';
import { useCheckPermission } from 'src/utils/checkPermission';
import { getUsersListFetch } from 'src/resources/usersList/reducer';
import { OptionType } from 'src/components/Select';
import { addMessage } from 'src/resources/alertMessages/reducer';
import { FormErrorsType, OnChangeType } from '../../types/global';

export const EDIT_KEYS = [
    'TITLE',
    'TEXT',
    'EXECUTE_AT',
    'IS_PUSH_NOTIFICATION',
    'IS_EMAIL_NOTIFICATION',
    'ATTENDEES_USERS',
    'attendeesUsers',
];

export type EditKeysType =
    | 'TITLE'
    | 'TEXT'
    | 'EXECUTE_AT'
    | 'IS_PUSH_NOTIFICATION'
    | 'IS_EMAIL_NOTIFICATION'
    | 'ATTENDEES_USERS'
    | 'attendeesUsers';

export interface ReminderDetailContainerPropsType {
    idReminder?: number;
    type: 'order' | 'return' | 'claim' | 'receiving';
    idItem?: string;
    onClose: () => void;
    setOpenId: (id: number) => void;
}

const initData = { IS_PUSH_NOTIFICATION: 1, IS_EMAIL_NOTIFICATION: 1 };

const ReminderDetailContainer: FC<ReminderDetailContainerPropsType> = (props) => {
    const { idReminder } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const formRef = useRef<HTMLFormElement>(null);
    const newReminder = !idReminder;

    const data = useSelector((state: RootStoreType) => state.reminderDetail.data);
    const canceled = useSelector((state: RootStoreType) => state.reminderDetail.canceled);
    const removedFromAttendees = useSelector((state: RootStoreType) => state.reminderDetail.removedFromAttendees);
    const saved = useSelector((state: RootStoreType) => state.reminderDetail.saved);
    const usersData = useSelector((state: RootStoreType) => state.usersList.data);
    const userData = useSelector((state: RootStoreType) => state.user.data);

    const [editData, setEditData] = useState<ReminderType>(
        getEditData(
            {
                ...(initData || {}),
                TYPE_ITEM: props.type,
                ID_ITEM: Number(props.idItem),
                attendeesUsers: userData ? [userData.USER_ID] : [],
            },
            EDIT_KEYS
        )
    );
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [usersOptions, setUsersOptions] = useState<OptionType[]>([]);

    const permissionCreate = useCheckPermission('REMINDER.CREATE');
    const permissionUpdate = useCheckPermission('REMINDER.UPDATE');

    const disableEdit = newReminder ? !permissionCreate : !permissionUpdate || !data?.isCreator;

    useEffect(() => {
        if (idReminder) {
            dispatch(getReminderDetailFetch(idReminder));
        }
        return () => {
            dispatch(clearReminderDetail());
        };
    }, [idReminder]);

    useEffect(() => {
        if (data) {
            if (newReminder && data.ID) {
                // props.setOpenId(data.ID);
            } else {
                setEditData({
                    ...data,
                    attendeesUsers: data.ATTENDEES_USERS?.map((item) => item.USER_ID),
                });
            }
        }
    }, [data]);

    useEffect(() => {
        if (saved) {
            props.onClose();
        }
    }, [saved]);

    useEffect(() => {
        if (!usersData) {
            dispatch(getUsersListFetch());
        } else {
            setUsersOptions(
                usersData
                    .filter((user) => user.active && !user.notActualProvider)
                    .map((user) => ({
                        value: user.USER_ID,
                        label: `${user.NAME} ${user.SURNAME}`,
                    }))
            );
        }
    }, [usersData]);

    useEffect(() => {
        if (canceled || (removedFromAttendees && data?.isCreator)) {
            handleClose();
        }
    }, [canceled, removedFromAttendees]);

    const handleClose = () => {
        props.onClose();
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        if (key === 'ATTENDEES_USERS') {
        } else {
            newData[key] = value;
        }
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const formatData = {
                ...editData,
                ATTENDEES_USERS: editData.attendeesUsers?.map((item) => ({ USER_ID: item })),
            };
            if (newReminder) {
                dispatch(createReminderDetailFetch({ ...formatData, ID_ITEM: props.idItem, TYPE_ITEM: props.type }));
            } else {
                dispatch(updateReminderDetailFetch({ idReminder, data: formatData }));
            }
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    const handleCancel = () => {
        dispatch(cancelReminderDetailFetch(idReminder));
    };
    const handleRemoveFromReminderAttendees = () => {
        dispatch(removeFromReminderAttendeesFetch(idReminder));
    };

    return (
        <Dialog
            maxWidth="xl"
            title={translate(`reminderDetail.title.${props.type}`, { idReminder, idItem: props.idItem })}
            dontClose
            actions={
                <>
                    {/* {!newReminder && (
                        <Button variant="outlined" onClick={handleRemoveFromReminderAttendees}>
                            {translate('reminderDetail.action.removeFromReminderAttendees')}
                        </Button>
                    )} */}
                    {!newReminder && (
                        <Button variant="outlined" onClick={handleCancel}>
                            {translate('reminderDetail.action.cancel')}
                        </Button>
                    )}
                    <Button variant="outlined" onClick={handleClose}>
                        {translate('reminderDetail.action.close')}
                    </Button>
                    <Button variant="contained" onClick={handleSave}>
                        {translate(newReminder ? 'reminderDetail.action.create' : 'reminderDetail.action.save')}
                    </Button>
                </>
            }
        >
            <form ref={formRef}>
                <FormBlock>
                    <Detail
                        data={data}
                        onChange={onChange}
                        editData={editData}
                        editErrors={editErrors}
                        newReminder={newReminder}
                        disableEdit={disableEdit}
                        usersOptions={usersOptions}
                    />
                </FormBlock>
            </form>
        </Dialog>
    );
};

export default ReminderDetailContainer;
